import clsx from "clsx";
import type { FC } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../../../app/modules/auth";
import { checkIsActive, KTIcon, type WithChildren } from "../../../helpers";

type Props = {
	to: string;
	title: string;
	icon?: string;
	fontIcon?: string;
	hasBullet?: boolean;
	userRoles?: number[];
	exact?: boolean;
};

const AsideMenuItem: FC<Props & WithChildren> = ({
	children,
	to,
	title,
	icon,
	fontIcon,
	userRoles,
	exact = false,
	hasBullet = false,
}) => {
	const { pathname } = useLocation();
	const { currentUser } = useAuth();
	const isActive = checkIsActive(pathname, to, exact);

	// If userRoles is defined and currentUser's role_id is not in userRoles, hide the menu item
	if (
		userRoles &&
		currentUser?.role_id &&
		!userRoles.includes(currentUser.role_id)
	) {
		return null;
	}

	// TEMPORARILY DISABLED FOR TESTING (Change flag to true after testing to re-enable)
	const ENABLE_PROFILE_INCOMPLETE_CHECK = false;

	const isProfileIncomplete =
		ENABLE_PROFILE_INCOMPLETE_CHECK &&
		currentUser?.role_id !== 1 &&
		!(
			Number(currentUser?.profile_step) === 5 &&
			currentUser?.profile_status?.trim().toLowerCase() === "completed"
		) &&
		(to.includes("/registration/vrp") || to.includes("/scheme-data-entry"));

	const handleClick = (e: React.MouseEvent) => {
		if (isProfileIncomplete) {
			e.preventDefault();
			Swal.fire({
				icon: "warning",
				title: "Profile Incomplete",
				text: "Please complete your profile registration first before proceeding.",
				confirmButtonColor: "#131c5d",
			});
		}
	};

	return (
		<div className="menu-item">
			<Link
				className={clsx("menu-link without-sub", { active: isActive })}
				to={to}
				onClick={handleClick}
			>
				{hasBullet && (
					<span className="menu-bullet">
						<span className="bullet bullet-dot"></span>
					</span>
				)}
				{icon && (
					<span className="menu-icon">
						<KTIcon iconName={icon} className="fs-2" />
					</span>
				)}
				{fontIcon && <i className={clsx("bi fs-3", fontIcon)}></i>}
				<span className="menu-title">{title}</span>
			</Link>
			{children}
		</div>
	);
};

export { AsideMenuItem };
